import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Plus, Calendar, Award, Users, Clock, Megaphone } from 'lucide-react';
import { useNotification } from '../hooks/useNotification';
import { NotificationContainer } from '../components/NotificationContainer';
import { CreateCampaignModal } from '../components/CreateCampaignModal';
import { EditCampaignModal } from '../components/EditCampaignModal';
import { ShareModal } from '../components/ShareModal';
import { CoverImageModal } from '../components/CoverImageModal';
import { CampaignCard } from '../components/CampaignCard';
import { DashboardHeader } from '../components/DashboardHeader';
import { CompleteCampaignModal } from '../components/campaign/CompleteCampaignModal';
import { apiUrl } from '../utils/api';
import { PushPermissionPrompt } from '../components/PushPermissionPrompt';

interface User {
  id: string;
  fullName: string;
  email: string;
}

interface Campaign {
  id: string;
  title: string;
  description: string;
  deadline: string;
  location: { city: string; state: string };
  privacyMode: 'public' | 'private';
  status: 'active' | 'expired' | 'completed' | 'cancelled';
  completionMessage?: string;
  createdAt?: string;
  progressPercentage?: number;
  totalProducts?: number;
  fullyCommittedProducts?: number;
  contactInfo?: { phone: string; email: string };
  coverImageUrl?: string;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  const [shareCampaign, setShareCampaign] = useState<Campaign | null>(null);
  const [coverCampaign, setCoverCampaign] = useState<Campaign | null>(null);
  const [completeCampaign, setCompleteCampaign] = useState<Campaign | null>(null);
  const { notifications, addNotification, removeNotification } = useNotification();

  const refreshCampaigns = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoading(true);
      await loadCampaigns(token);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/login'); return; }
    try { setUser(JSON.parse(userData)); } catch { navigate('/login'); return; }
    if (searchParams.get('created') === 'true') {
      addNotification({ type: 'success', title: '¡Campaña creada!', message: 'Tu campaña se creó exitosamente' });
    }
    loadCampaigns(token);
  }, [navigate, searchParams]);

  const loadCampaigns = async (token: string) => {
    try {
      const response = await fetch(apiUrl('/api/campaigns/my-campaigns?withProgress=true'), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setCampaigns(data.data || []);
      } else if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } catch { /* silent */ }
    finally { setIsLoading(false); }
  };

  const isExpired = (d: string) => new Date(d) < new Date();
  const isDeadlineSoon = (d: string) => {
    const diff = Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff <= 7 && diff > 0;
  };

  const activeCampaigns = campaigns.filter(c => c.status === 'active' && !isExpired(c.deadline));
  const completedCampaigns = campaigns.filter(c => c.status === 'completed');
  const soonCampaigns = campaigns.filter(c => c.status === 'active' && isDeadlineSoon(c.deadline));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <DashboardHeader
        userName={user?.fullName || ''}
        onCreateCampaign={() => setShowCreateModal(true)}
      />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Megaphone className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-500">Activas</p>
                <p className="text-xl font-bold text-gray-900">{activeCampaigns.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-green-50 rounded-lg">
                <Award className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-500">Completadas</p>
                <p className="text-xl font-bold text-gray-900">{completedCampaigns.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-xl font-bold text-gray-900">{campaigns.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-amber-50 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs text-gray-500">Por vencer</p>
                <p className="text-xl font-bold text-gray-900">{soonCampaigns.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns */}
        {campaigns.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 text-center py-16">
            <Megaphone className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Sin campañas aún</h3>
            <p className="mt-1 text-sm text-gray-500">Crea tu primera campaña y comienza a recibir donaciones.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" /> Crear campaña
            </button>
          </div>
        ) : (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Mis Campañas</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onShare={(c) => setShareCampaign(c)}
                  onEdit={(c) => setEditCampaign(c)}
                  onCover={(c) => setCoverCampaign(c)}
                  onComplete={(c) => setCompleteCampaign(c)}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              <img src="/isotipo.png" alt="DonaApp" className="h-6 w-6" />
              <span className="text-xs text-gray-400">DonaApp — OVIEDOTECH S.A.S.</span>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <Link to="/acerca-de" className="hover:text-indigo-400 transition-colors">Acerca de</Link>
              <Link to="/faq" className="hover:text-indigo-400 transition-colors">FAQ</Link>
              <Link to="/terminos-y-condiciones" className="hover:text-indigo-400 transition-colors">Términos</Link>
              <Link to="/politica-de-privacidad" className="hover:text-indigo-400 transition-colors">Privacidad</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Push notification prompt */}
      <PushPermissionPrompt />

      {/* Modals */}
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          refreshCampaigns();
          addNotification({ type: 'success', title: '¡Campaña creada!', message: 'Tu campaña se creó exitosamente' });
        }}
      />

      <EditCampaignModal
        isOpen={!!editCampaign}
        onClose={() => setEditCampaign(null)}
        onSuccess={() => {
          refreshCampaigns();
          addNotification({ type: 'success', title: 'Campaña actualizada', message: 'Los cambios se guardaron correctamente' });
        }}
        campaign={editCampaign}
      />

      <ShareModal
        isOpen={!!shareCampaign}
        onClose={() => setShareCampaign(null)}
        campaignId={shareCampaign?.id || ''}
        campaignTitle={shareCampaign?.title || ''}
      />

      <CoverImageModal
        isOpen={!!coverCampaign}
        onClose={() => setCoverCampaign(null)}
        onSuccess={() => {
          refreshCampaigns();
          addNotification({ type: 'success', title: 'Portada actualizada', message: 'La imagen se subió correctamente' });
        }}
        campaignId={coverCampaign?.id || ''}
        currentImageUrl={coverCampaign?.coverImageUrl}
      />

      <CompleteCampaignModal
        isOpen={!!completeCampaign}
        campaignId={completeCampaign?.id || ''}
        campaignTitle={completeCampaign?.title || ''}
        onClose={() => setCompleteCampaign(null)}
        onSuccess={() => {
          setCompleteCampaign(null);
          refreshCampaigns();
          addNotification({ type: 'success', title: '¡Campaña finalizada!', message: 'Tu campaña fue cerrada. Sube evidencia para generar confianza.' });
        }}
      />

      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </div>
  );
}
